import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { enqueueSnackbar } from 'notistack';

import { useAppDispatch, useAppSelector } from 'modules/state-mgmt/store';
import {
  authenticateUser,
  authenticateUserInAdminSession,
  getUserCompanies,
  initiateauth,
  respondToAuthChallenge,
} from 'modules/state-mgmt/user';
import { fetchWorkerDetails } from 'modules/state-mgmt/onboarding';
import useVerifyAdminContext from 'hooks/useVerifyAdminContext';
import useResumeProjectOnboarding from 'hooks/useResumeProjectOnboarding';
import { prettifyErrorResponse } from 'utils';

import { ROUTES } from 'constants/routes';
import { STYLE } from 'constants/style';

const config = process.env;
interface IUseLogin {
  onSuccess?: () => void;
  onFailure?: (backendErrors: any) => void;
}

interface Company {
  id: string;
  name: string;
  companyLabel: number;
  isUserAWorkerInCompany: boolean;
  companyUserId: string;
  companyUserType: number;
  isVerified: boolean;
}

export const useLogin = ({ onSuccess, onFailure }: IUseLogin) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [authData, setAuthData] = useState<{
    username: string;
    password: string;
    isAdminSession: boolean;
    authResponse?: any;
  } | null>(null);
  const { onboarding, user, languageCode } = useAppSelector(state => ({
    onboarding: state.onboarding,
    user: state.user,
    languageCode: state.app?.languageCode,
  }));

  const { verifyAndRestore } = useVerifyAdminContext();
  // CH-4652 lives in its own hook because the OTP sign-in path needs exactly the same behaviour.
  const { isResuming, resumeProjectOnboarding } = useResumeProjectOnboarding({
    onSettled: () => setIsLoading(false),
  });

  const login = async (username: string, password: string) => {
    setIsLoading(true);

    try {
      let workerLoginData = {} as any;
      const isAdminSession = (user.companyUserId || user.userId) && !user.isWorker;
      // Authenticate user
      if (isAdminSession) {
        // Worker login in admin session
        workerLoginData = await dispatch(authenticateUserInAdminSession({ username, password })).unwrap();
      } else {
        // Normal worker login using cognito
        workerLoginData = await dispatch(authenticateUser({ username, password })).unwrap();
      }

      // Check if user is a worker
      if (workerLoginData?.isWorker) {
        workerLoginData.workerId &&
          (await dispatch(
            fetchWorkerDetails({ workerId: workerLoginData.workerId, languageCode: languageCode }),
          ).unwrap());
        // Must run before the resume: it can `resetCache()`, which would wipe the project and the
        // profile-field constraints the resume routes on.
        await verifyAndRestore();
        onSuccess?.();

        const fallbackPath = isAdminSession ? ROUTES.PROJECT_LIST.path : '/';
        // CH-4652 — when the duplicate check sent this worker here, tie them to the project that
        // was selected beforehand and continue onboarding rather than dumping them on My Projects.
        if (await resumeProjectOnboarding(fallbackPath)) return;

        setIsLoading(false);
        navigate(fallbackPath);
      } else {
        //! This code will never get executed since we are only allowing workers to login through cognito and admin direct login is blocked. Keeping this code here for future reference in case we want to allow non-worker logins in the future. If that happens we will also need to remove the block on admin direct login in the backend and handle admin logins through cognito like workers instead of having a separate flow for admin sessions.
        // Non-worker login - check for multiple companies
        const userId = workerLoginData.userId || workerLoginData.companyUserId;
        const originalCompanyId = workerLoginData.currentCompanyId;
        const companiesResponse = await dispatch(getUserCompanies(userId)).unwrap();

        if (companiesResponse && companiesResponse.length > 1) {
          // Multiple companies - show company selection modal
          setCompanies(companiesResponse);
          setAuthData({ username, password, isAdminSession, authResponse: workerLoginData });
          setShowCompanyModal(true);
          setIsLoading(false);
        } else if (companiesResponse && companiesResponse.length === 1) {
          const singleCompanyId = companiesResponse[0].id;
          if (singleCompanyId === originalCompanyId) {
            workerLoginData.workerId &&
              (await dispatch(
                fetchWorkerDetails({ workerId: workerLoginData.workerId, languageCode: languageCode }),
              ).unwrap());
            await verifyAndRestore();
            setIsLoading(false);
            onSuccess?.();
            navigate(isAdminSession ? ROUTES.PROJECT_LIST.path : '/');
          } else {
            await handleCompanySelection(singleCompanyId, username, password, isAdminSession, workerLoginData);
          }
        } else {
          // No companies found
          workerLoginData.workerId &&
            (await dispatch(
              fetchWorkerDetails({ workerId: workerLoginData.workerId, languageCode: languageCode }),
            ).unwrap());
          await verifyAndRestore();
          setIsLoading(false);
          onSuccess?.();
          navigate(isAdminSession ? ROUTES.PROJECT_LIST.path : '/');
        }
      }
    } catch (error: any) {
      setIsLoading(false);
      onFailure?.(error);
      if (error?.code === 'ADMIN_DIRECT_LOGIN_BLOCKED') {
        const url = error.freedomWebUrl;
        const linkStyle = {
          color: STYLE.COLOR.WHITE,
          textDecoration: 'underline',
          fontWeight: 'bold' as const,
        };
        const link = React.createElement(
          'a',
          { href: url, target: '_blank', rel: 'noopener noreferrer', style: linkStyle },
          url,
        );
        const message = React.createElement('span', null, error.message + ' ', link);
        enqueueSnackbar(message, { variant: 'error' });
      } else {
        enqueueSnackbar(prettifyErrorResponse(error), { variant: 'error' });
      }
    }
  };

  const handleCompanySelection = async (
    companyId: string,
    username?: string,
    password?: string,
    isAdminSession?: boolean,
    initialAuthData?: any,
  ) => {
    setIsLoading(true);

    try {
      const user = username || authData?.username;
      const pass = password || authData?.password;
      const adminSession = isAdminSession !== undefined ? isAdminSession : authData?.isAdminSession || false;
      const authResponse = initialAuthData || authData?.authResponse;
      const originalCompanyId = authResponse?.currentCompanyId;

      if (originalCompanyId && originalCompanyId === companyId) {
        authResponse.workerId &&
          (await dispatch(
            fetchWorkerDetails({ workerId: authResponse.workerId, languageCode: languageCode }),
          ).unwrap());
        await verifyAndRestore();
        setIsLoading(false);
        onSuccess?.();
        navigate(adminSession ? ROUTES.PROJECT_LIST.path : '/');
        return;
      }

      if (!user || !pass || !authResponse?.username) {
        throw new Error('Authentication data is missing');
      }

      const userData = {
        username: authResponse.username,
        clientId: config.REACT_APP_AWS_USER_POOL_WEB_CLIENT_ID,
        customauthchallenge: '1',
      };

      const { session } = await dispatch(initiateauth(userData)).unwrap();

      await dispatch(
        respondToAuthChallenge({
          ...userData,
          SelectedCompany: companyId,
          session: session,
        }),
      ).unwrap();

      // Re Authenticate to update the session with new company context
      let workerLoginData = {} as any;
      if (isAdminSession) {
        // Worker login in admin session
        workerLoginData = await dispatch(authenticateUserInAdminSession({ username, password })).unwrap();
      } else {
        // Normal worker login using cognito
        workerLoginData = await dispatch(authenticateUser({ username, password })).unwrap();
      }

      if (workerLoginData.workerId) {
        await dispatch(
          fetchWorkerDetails({
            workerId: workerLoginData.workerId,
            languageCode: languageCode,
          }),
        ).unwrap();
      }
      await verifyAndRestore();
      setIsLoading(false);
      setShowCompanyModal(false);
      onSuccess?.();
      navigate(adminSession ? ROUTES.PROJECT_LIST.path : '/');
    } catch (error) {
      setIsLoading(false);
      setShowCompanyModal(false);
      onFailure?.(error);
      enqueueSnackbar(prettifyErrorResponse(error), { variant: 'error' });
    }
  };

  return {
    isLoading,
    isResuming,
    login,
    companies,
    showCompanyModal,
    handleCompanySelection,
    closeCompanyModal: () => {
      setShowCompanyModal(false);
      setAuthData(null);
      setCompanies([]);
    },
  };
};
